import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TerrasdeurOptionsPanel from '../components/TerrasdeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  raamtypes, 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING 
} from '../data/mockData';
import {
  applyDoorLayoutFieldChange,
} from '../lib/partWidthDistribution';
import {
  applyDoorTypeChange,
  VOORDEUR_TOP_LIGHT_HEIGHT_MM,
  VOORDEUR_SIDE_PANEL_MM,
  DOOR_MIN_DOOR_HEIGHT_MM,
  DOOR_NORMAL_MAX_HEIGHT,
  DOOR_MAX_DOOR_HEIGHT,
  DOOR_MIN_OVERHEAD_HEIGHT,
  DOOR_MAX_TOTAL_HEIGHT,
  SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM,
  TERRASDEUR_MIN_DOOR_LEAF_MM,
  TERRASDEUR_MAX_DOOR_LEAF_MM,
  getTerrasdeurMinTotalWidth,
  getTerrasdeurMaxTotalWidth,
  applyTerrasdeurDesignTypeChange,
  applyTerrasdeurParapetSync,
  applyTerrasdeurSidePanelParapetSync,
  applyTerrasdeurSidePanelOverheadSync,
  clearTerrasdeurSideParapets,
  finalizeTerrasdeurUpdate,
  enforceTerrasdeurWidthBounds,
  enforceDoorHeightBounds,
} from '../lib/voordeurConfigHelpers';
import {
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import {
  applyPersistedConfiguratorPreferences,
  trackSharedConfiguratorChange,
} from '../lib/configuratorGlazing';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';

const TERRASDEUR_MIN_SIDE_PANEL_WIDTH = VOORDEUR_SIDE_PANEL_MM;
const TERRASDEUR_MIN_TOTAL_HEIGHT = DOOR_MIN_DOOR_HEIGHT_MM;

const TERRASDEUR_LAYOUT_OPTIONS = {
  minDoorWidth: TERRASDEUR_MIN_DOOR_LEAF_MM,
  minPanelWidth: VOORDEUR_SIDE_PANEL_MM,
};

const DEFAULT_TERRASDEUR_CONFIG = {
  width: 1400,
  height: 2200,
  designType: 'no-panel',
  doorType: 'normal',
  profileSystem: 'schuco',
  handlePosition: 'left',
  thresholdHeight: '30mm',
  glassTexture: 'clear',
  glazingType: 'double-24',
  parapet: 'no-parapet',
  parapetHeight: 250,
  parapetColor: 'interior',
  panelType: 'sandwich-no-mullions',
  leftPanelParapetPanelType: 'sandwich-no-mullions',
  rightPanelParapetPanelType: 'sandwich-no-mullions',
  securityBracket: 'none',
  interiorColor: 'ral-9016',
  exteriorColor: 'anthracite',
  woodJoint: 'joint-45',
  leftPanelWidth: undefined,
  rightPanelWidth: undefined,
  leftPanelWindowType: 'fixed-sash',
  rightPanelWindowType: 'fixed-sash',
  leftPanelParapet: false,
  rightPanelParapet: false,
  leftPanelParapetHeight: 250,
  rightPanelParapetHeight: 250,
  leftPanelParapetColor: undefined,
  rightPanelParapetColor: undefined,
  leftPanelOverhead: false,
  rightPanelOverhead: false,
  leftPanelOverheadWindowType: 'fixed-sash',
  rightPanelOverheadWindowType: 'fixed-sash',
  leftPanelOverheadHeight: 300,
  rightPanelOverheadHeight: 300,
  overheadParts: 1,
  overheadPartWindowTypes: ['fixed-sash'],
  overheadHeight: 300,
  quantity: 1
};

const Terrasdeur = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    enforceTerrasdeurWidthBounds(
      withDefaultAdditionalItems(
        applyPersistedConfiguratorPreferences(DEFAULT_TERRASDEUR_CONFIG, DOOR_COLOR_FIELDS)
      )
    );

  const { config, setConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) =>
      enforceTerrasdeurWidthBounds(
        withDefaultAdditionalItems(loaded)
      )
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    trackSharedConfiguratorChange(field, value);
    markDirty();
    setConfig((prev) => {
      if (field === 'designType') {
        return finalizeTerrasdeurUpdate(
          enforceTerrasdeurWidthBounds(
            applyTerrasdeurSidePanelParapetSync(
              applyTerrasdeurSidePanelOverheadSync(
                applyTerrasdeurDesignTypeChange(prev, value)
              )
            )
          )
        );
      }
      if (field === 'doorType') {
        return { ...prev, ...applyDoorTypeChange(prev, value) };
      }

      if (field === 'parapet') {
        if (value === 'with-parapet') {
          return applyTerrasdeurParapetSync({ ...prev, parapet: value });
        }
        return enforceTerrasdeurWidthBounds(
          clearTerrasdeurSideParapets({ ...prev, parapet: value })
        );
      }

      if (
        prev.parapet === 'with-parapet' &&
        (field === 'parapetHeight' || field === 'panelType' || field === 'parapetColor')
      ) {
        const override =
          field === 'parapetHeight'
            ? { parapetHeight: Math.round(Number(value) || prev.parapetHeight || 250) }
            : { [field]: value };
        return applyTerrasdeurParapetSync(prev, override);
      }

      if (prev.parapet === 'with-parapet' && field.includes('PanelParapet')) {
        if (field === 'leftPanelParapetHeight' || field === 'rightPanelParapetHeight') {
          return applyTerrasdeurParapetSync(prev, {
            parapetHeight: Math.round(Number(value) || prev.parapetHeight || 250),
          });
        }
        if (field === 'leftPanelParapetPanelType' || field === 'rightPanelParapetPanelType') {
          return applyTerrasdeurParapetSync(prev, { panelType: value });
        }
        if (field === 'leftPanelParapetColor' || field === 'rightPanelParapetColor') {
          return applyTerrasdeurParapetSync(prev, { parapetColor: value });
        }
        return applyTerrasdeurParapetSync(prev);
      }

      if (
        field === 'leftPanelParapet' ||
        field === 'rightPanelParapet' ||
        field === 'leftPanelParapetHeight' ||
        field === 'rightPanelParapetHeight' ||
        field === 'leftPanelParapetPanelType' ||
        field === 'rightPanelParapetPanelType' ||
        field === 'leftPanelParapetColor' ||
        field === 'rightPanelParapetColor'
      ) {
        if (field === 'leftPanelParapetHeight' || field === 'rightPanelParapetHeight') {
          const parapetHeight = Math.round(Number(value) || prev[field] || 250);
          return applyTerrasdeurSidePanelParapetSync(prev, {
            leftPanelParapetHeight: parapetHeight,
            rightPanelParapetHeight: parapetHeight,
          });
        }
        if (field === 'leftPanelParapetPanelType' || field === 'rightPanelParapetPanelType') {
          return applyTerrasdeurSidePanelParapetSync(prev, {
            leftPanelParapetPanelType: value,
            rightPanelParapetPanelType: value,
          });
        }
        if (field === 'leftPanelParapetColor' || field === 'rightPanelParapetColor') {
          return applyTerrasdeurSidePanelParapetSync(prev, {
            leftPanelParapetColor: value,
            rightPanelParapetColor: value,
          });
        }
        return applyTerrasdeurSidePanelParapetSync({ ...prev, [field]: value });
      }

      if (field === 'width') {
        return enforceTerrasdeurWidthBounds({
          ...prev,
          width: Math.round(Number(value) || prev.width),
        });
      }

      if (field === 'height') {
        const numericHeight = Math.round(Number(value) || prev.height);
        if (numericHeight > DOOR_NORMAL_MAX_HEIGHT) {
          return enforceDoorHeightBounds({
            ...prev,
            doorType: 'top-light',
            height: numericHeight,
            overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
          });
        }
        if (prev.doorType === 'top-light') {
          return {
            ...prev,
            doorType: 'normal',
            height: numericHeight,
          };
        }
        return { ...prev, height: numericHeight };
      }

      if (field === 'overheadHeight') {
        return enforceDoorHeightBounds({
          ...prev,
          overheadHeight: Math.round(Number(value) || prev.overheadHeight || VOORDEUR_TOP_LIGHT_HEIGHT_MM),
        });
      }

      if (field === 'leftPanelOverheadWindowType' || field === 'rightPanelOverheadWindowType') {
        return { ...prev, [field]: value };
      }

      if (field === 'leftPanelOverhead' || field === 'rightPanelOverhead') {
        return applyTerrasdeurSidePanelOverheadSync({ ...prev, [field]: value });
      }

      if (field === 'leftPanelOverheadHeight' || field === 'rightPanelOverheadHeight') {
        const overheadHeight = Math.min(
          SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM,
          Math.round(Number(value) || prev[field] || VOORDEUR_TOP_LIGHT_HEIGHT_MM)
        );
        return applyTerrasdeurSidePanelOverheadSync(prev, {
          leftPanelOverheadHeight: overheadHeight,
          rightPanelOverheadHeight: overheadHeight,
        });
      }

      if (field === 'additionalItems') {
        return { ...prev, additionalItems: value };
      }

      const layoutUpdate = applyDoorLayoutFieldChange(
        prev,
        field,
        value,
        TERRASDEUR_LAYOUT_OPTIONS
      );
      if (layoutUpdate) {
        return finalizeTerrasdeurUpdate(
          enforceTerrasdeurWidthBounds({ ...prev, ...layoutUpdate })
        );
      }

      return finalizeTerrasdeurUpdate({ ...prev, [field]: value });
    });
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleOverheadPartWindowTypeChange = (index, windowType) => {
    setConfig(prev => {
      const currentParts = prev.doorType === 'top-light' ? (prev.overheadParts || 1) : 1;
      const currentWindowTypes = prev.overheadPartWindowTypes || Array(currentParts).fill('fixed-sash');
      const newWindowTypes = [...currentWindowTypes];
      
      while (newWindowTypes.length <= index) {
        newWindowTypes.push('fixed-sash');
      }
      
      newWindowTypes[index] = windowType;
      
      return {
        ...prev,
        overheadPartWindowTypes: newWindowTypes
      };
    });
  };

  return (
    <ConfiguratorShell
      title={t('configurator.terraceDoorConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <TerrasdeurOptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={
        <DoorDrawingBoard
          width={config.width}
          height={config.height}
          designType={config.designType}
          doorType={config.doorType}
          handlePosition={config.handlePosition}
          exteriorColor={config.exteriorColor}
          interiorColor={config.interiorColor}
          glassTexture={config.glassTexture}
          woodJoint={config.woodJoint}
          parapet={config.parapet}
          parapetHeight={config.parapetHeight}
          parapetColor={config.parapetColor}
          panelType={config.panelType}
          leftPanelWidth={config.leftPanelWidth}
          rightPanelWidth={config.rightPanelWidth}
          leftPanelWindowType={config.leftPanelWindowType}
          rightPanelWindowType={config.rightPanelWindowType}
          onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
          onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
          leftPanelParapet={config.leftPanelParapet}
          rightPanelParapet={config.rightPanelParapet}
          leftPanelParapetHeight={config.leftPanelParapetHeight}
          rightPanelParapetHeight={config.rightPanelParapetHeight}
          leftPanelParapetColor={config.leftPanelParapetColor}
          rightPanelParapetColor={config.rightPanelParapetColor}
          leftPanelParapetPanelType={config.leftPanelParapetPanelType}
          rightPanelParapetPanelType={config.rightPanelParapetPanelType}
          onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
          onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
          onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
          onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
          onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
          onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
          onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
          onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
          sidePanelOverheadEnabled
          leftPanelOverhead={config.leftPanelOverhead}
          rightPanelOverhead={config.rightPanelOverhead}
          leftPanelOverheadWindowType={config.leftPanelOverheadWindowType}
          rightPanelOverheadWindowType={config.rightPanelOverheadWindowType}
          leftPanelOverheadHeight={config.leftPanelOverheadHeight}
          rightPanelOverheadHeight={config.rightPanelOverheadHeight}
          onLeftPanelOverheadChange={(value) => handleConfigChange('leftPanelOverhead', value)}
          onRightPanelOverheadChange={(value) => handleConfigChange('rightPanelOverhead', value)}
          onLeftPanelOverheadWindowTypeChange={(value) => handleConfigChange('leftPanelOverheadWindowType', value)}
          onRightPanelOverheadWindowTypeChange={(value) => handleConfigChange('rightPanelOverheadWindowType', value)}
          onLeftPanelOverheadHeightChange={(value) => handleConfigChange('leftPanelOverheadHeight', value)}
          onRightPanelOverheadHeightChange={(value) => handleConfigChange('rightPanelOverheadHeight', value)}
          overheadPartWindowTypes={config.overheadPartWindowTypes || ['fixed-sash']}
          onOverheadPartWindowTypeChange={handleOverheadPartWindowTypeChange}
          overheadHeight={config.overheadHeight}
          onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
          onParapetHeightChange={(value) => handleConfigChange('parapetHeight', value)}
          onWidthChange={(value) => handleConfigChange('width', value)}
          onHeightChange={(value) => handleConfigChange('height', value)}
          onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
          onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
          onDoorSectionWidthChange={(value) => handleConfigChange('doorSectionWidth', value)}
          onGlassTextureChange={(value) => handleConfigChange('glassTexture', value)}
          minWidth={getTerrasdeurMinTotalWidth(config.designType)}
          maxWidth={getTerrasdeurMaxTotalWidth(config.designType)}
          maxDoorLeafWidth={TERRASDEUR_MAX_DOOR_LEAF_MM}
          minDoorLeafWidth={TERRASDEUR_MIN_DOOR_LEAF_MM}
          minSidePanelWidth={TERRASDEUR_MIN_SIDE_PANEL_WIDTH}
          minHeight={TERRASDEUR_MIN_TOTAL_HEIGHT}
          maxHeight={DOOR_MAX_TOTAL_HEIGHT}
          maxDoorHeight={DOOR_MAX_DOOR_HEIGHT}
          minDoorHeight={DOOR_MIN_DOOR_HEIGHT_MM}
          minOverheadHeight={DOOR_MIN_OVERHEAD_HEIGHT}
          maxSidePanelOverheadHeight={SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={raamtypes}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="terrasdeur"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={markClean}
        />
      }
    />
  );
};

export default Terrasdeur;
